/*Service*/
import {
  SystemCode,
  DeleteSystemCodeRequest,
} from '../../../proto/admpb/systemcode_grpc_web_pb';

/*ReactJS*/
import React, { useState, useEffect, useContext } from 'react';
import { CSVLink } from 'react-csv';

/*Toast Notification*/
import {
  notifySuccess,
  notifyInfo,
  notifyError,
} from 'components/Notification/Notification';

/*Material UI Components*/
import {
  TableCell,
  TableRow,
  Typography,
  Checkbox,
  IconButton,
  Button,
  Box,
  TextField,
  Modal,
  Backdrop,
  Fade,
} from '@material-ui/core';

/*Material UI Icons*/
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Create as EditIcon,
} from '@material-ui/icons';

/*Material UI Confirm*/
import { useConfirm } from 'material-ui-confirm';

/*Material UI Table*/
import MUIDataTable from 'mui-datatables';

/*Styles*/
import tableTheme from '../../../components/Table/TableStyle';
import { MuiThemeProvider } from '@material-ui/core/styles';
import useStyles from '../../../styles';

/*Custom component*/
import SearchButton from '../../../components/Button/Search';
import SelectSystemCodeType from '../../../components/AutoComplete/SystemCodeType';

/*Moment JS*/
import moment from 'moment-timezone';
import { formatPbDate } from 'lib/fmt';

import { ServiceContext } from 'context/ServiceContext';

/*=========================================================================================
Add and Update system code function
===========================================================================================*/
function saveSystemCode(
  systemCodeClient,
  rows,
  isAdd,
  closeModal,
  systemCodeID,
  type,
  code,
  codeDescription
) {
  if (isAdd) {
    let req = new SystemCode();
    req.setType(type);
    req.setCode(code);
    req.setDescription(codeDescription);

    if (!type) {
      notifyError('Type is a required field.');
      return;
    }

    systemCodeClient.createSystemCode(req, {}, (err, res) => {
      if (err) {
        console.error(err.message);
        notifyError(err.message);
        return;
      }
      rows.push(res.toObject().systemCode);
      notifySuccess('System code has been added.');
      closeModal();
    });
  } else {
    let req = new SystemCode();
    req.setSystemCodeId(systemCodeID);
    req.setType(type);
    req.setCode(code);
    req.setDescription(codeDescription);

    systemCodeClient.updateSystemCode(req, {}, (err) => {
      if (err) {
        console.error(err.message);
        notifyError(err.message);
        return;
      }
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].systemCodeId === systemCodeID) {
          rows[i].type = type;
          rows[i].code = code;
          rows[i].description = codeDescription;
          break;
        }
      }
      notifySuccess('System code has been updated.');
      closeModal();
    });
  }
}
/*=========================================================================================
End of add and update system code function
===========================================================================================*/

/*=========================================================================================
Delete system code function: Multiple
===========================================================================================*/
function removeSystemCode(
  systemCodeClient,
  rows,
  setRows,
  systemCodeIDs,
  handleClick,
  confirm
) {
  if (systemCodeIDs.length < 1) {
    notifyError('No item is selected.');
    return;
  }

  confirm({
    description:
      'You are about to delete ' +
      systemCodeIDs.length +
      ' System Codes in your system. Please confirm your action.',
    confirmationText: 'Yes, Delete',
  })
    .then(() => {
      systemCodeIDs.forEach(removeFunction);

      function removeFunction(item) {
        let req = new DeleteSystemCodeRequest();
        req.setSystemCodeId(item);

        systemCodeClient.deleteSystemCode(req, {}, (err) => {
          if (err) {
            console.error(err.message);
            notifyError(err.message);
          }

          let updatedData = [];

          for (let c = 0; c < rows.length; c++) {
            if (rows[c].systemCodeId === item) {
              rows.splice(rows[c], 1);
            } else {
              updatedData.push(rows[c]);
            }
          }
          setRows(updatedData);
        });
      }

      handleClick('', '', true);
      notifySuccess(systemCodeIDs.length + ' System codes has been deleted');
    })
    .catch(() => {
      /* ... */
    });
}
/*=========================================================================================
End of delete system code function: Multiple
===========================================================================================*/

/*=========================================================================================
Delete system code function: Single
===========================================================================================*/
function removeSystemCodeSingle(
  systemCodeClient,
  rows,
  setRows,
  systemCodeID,
  type,
  codeDescription,
  confirm
) {
  if (!codeDescription) {
    codeDescription = '';
  }

  confirm({
    description:
      "You are about to delete System Code '" +
      type +
      ': ' +
      codeDescription +
      "' in your system. Please confirm your action.",
    confirmationText: 'Yes, Delete',
  })
    .then(() => {
      let req = new DeleteSystemCodeRequest();
      req.setSystemCodeId(systemCodeID);

      systemCodeClient.deleteSystemCode(req, {}, (err) => {
        if (err) {
          console.error(err.message);
          notifyError(err.message);
          return;
        }

        let updatedData = [];

        for (let c = 0; c < rows.length; c++) {
          if (rows[c].systemCodeId === systemCodeID) {
            rows.splice(rows[c], 1);
          } else {
            updatedData.push(rows[c]);
          }
        }
        setRows(updatedData);
      });

      notifySuccess('System code has been deleted');
    })
    .catch(() => {
      // showToastMessage("error", "System error.");
    });
}
/*=========================================================================================
End of delete system code function: Single
===========================================================================================*/

export default function SystemCodeTable({ params }) {
  /*=========================================================================================
  Component style
  ===========================================================================================*/
  const classes = useStyles();
  const confirm = useConfirm();
  /*=========================================================================================
  End of component style
  ===========================================================================================*/

  const { systemCodeServiceClient: systemCodeClient } = useContext(
    ServiceContext
  );

  /*START OF PAGING LOGIC ===================================================================*/
  const [page, setPageState] = useState({
    currentPage: 0,
    displayedRows: 100,
    totalRows: 0,
    searchSrc: '',
    sortOrder: {},
    filter: [],
  });

  useEffect(() => {
    if (page.data) {
      /*sorting*/
      let sortField = page.sortOrder.name;
      let sortDir = page.sortOrder.direction;

      let fullData = page.data.sort((a, b) => {
        let val1 = a[sortField];
        let val2 = b[sortField];

        if (typeof val1 === 'object') {
          if (val1.seconds) {
            val1 = a[sortField]
              ? moment(new Date(a[sortField].seconds * 1000)).format(
                  'MM/DD/YYYY hh:mm'
                )
              : '--';
            val2 = b[sortField]
              ? moment(new Date(b[sortField].seconds * 1000)).format(
                  'MM/DD/YYYY hh:mm'
                )
              : '--';
          } else {
            val1 = formatPbDate(a[sortField]);
            val2 = formatPbDate(b[sortField]);
          }
        }
        if (val1 < val2) {
          return 1 * (sortDir === 'asc' ? -1 : 1);
        } else if (val1 > val2) {
          return -1 * (sortDir === 'asc' ? -1 : 1);
        } else {
          return 0;
        }
      });

      /*filter*/
      if (page.filter.length > 0) {
        let columnFilter = page.filter;
        let filterParams = {
          code:
            columnFilter[0].value !== undefined ? [columnFilter[0].value] : [],
          description:
            columnFilter[1].value !== undefined ? [columnFilter[1].value] : [],
          type:
            columnFilter[2].value !== undefined ? [columnFilter[2].value] : [],
        };

        const buildFilter = (filter) => {
          let query = {};
          for (let keys in filter) {
            if (filter[keys].constructor === Array && filter[keys].length > 0) {
              query[keys] = filter[keys];
            }
          }
          return query;
        };

        const filterData = (data, query) => {
          const filteredData = data.filter((item) => {
            for (let key in query) {
              if (item[key] === undefined || !query[key].includes(item[key])) {
                return false;
              }
            }
            return true;
          });
          return filteredData;
        };

        const query = buildFilter(filterParams);
        fullData = filterData(page.data, query);
      }

      var pageData = fullData.slice(
        page.currentPage * page.displayedRows,
        (page.currentPage + 1) * page.displayedRows
      );

      setRows(pageData);
      setDownloadableRows(fullData);

      if (page.searchSrc === 'search') {
        notifyInfo(page.totalRows + ' search results.');
      }

      setLoading(false);
    }
  }, [page]);

  /**search function to get balances ========================================================*/
  const search = (
    type,
    cPage,
    rowsPerPage,
    field,
    sortDirection,
    filterValue
  ) => {
    if (type === 'search') {
      const pathWithParams =
        currentPath + '?search=true' + (typeValue ? '&type=' + typeValue : '');
      window.history.pushState({}, null, pathWithParams);

      setLoading(true);

      let list = new SystemCode();
      list.setType(typeValue);

      systemCodeClient.listSystemCode(list, {}, (err, res) => {
        if (err) {
          console.error(err);
          notifyError(err.message);
          setLoading(false);
          return;
        }

        let fullData = res.toObject().systemCodesList;
        setPageState({
          ...page,
          totalRows: fullData.length,
          searchSrc: type,
          currentPage: cPage,
          displayedRows: rowsPerPage,
          data: fullData,
          sortOrder: { name: field, direction: sortDirection },
        });
      });
    } else {
      setPageState({
        ...page,
        searchSrc: type,
        currentPage: cPage,
        displayedRows: rowsPerPage,
        sortOrder: { name: field, direction: sortDirection },
        filter: filterValue,
      });
    }
  };
  /**search function end */

  /**PAGING LOGIC END ======================================================*/

  /*=========================================================================================
  Table Configuration
  ===========================================================================================*/
  const [rowID, setRowID] = React.useState('');
  const [selected, setSelected] = React.useState([]);

  const columns = [
    {
      id: '1',
      name: 'code',
      label: 'Code',
      visibleState: useState(true),
      align: 'left',
      order: 1,
    },
    {
      id: '2',
      name: 'description',
      label: 'Code Description',
      visibleState: useState(true),
      align: 'left',
      order: 2,
    },
    {
      id: '3',
      name: 'type',
      label: 'Type',
      visibleState: useState(true),
      align: 'left',
      order: 0,
    },
  ];

  const sortedColumns = columns.slice(0);
  sortedColumns.sort(function(a, b) {
    return a.order - b.order;
  });

  const setVisibles = {};
  columns.forEach((col) => {
    col.options = {
      display: col.visibleState[0],
    };
    setVisibles[col.name] = col.visibleState[1];
    return;
  });

  const handleClick = (event, systemCodeID, removed) => {
    if (!removed) {
      const selectedIndex = selected.indexOf(systemCodeID);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, systemCodeID);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
      setRowID(newSelected);
    } else {
      setSelected([]);
    }
  };

  const isSelected = (systemCodeID) => selected.indexOf(systemCodeID) !== -1;

  const customTableRow = (data, dataIndex, rowIndex) => {
    if (!rows[rowIndex]) {
      return;
    }

    const isItemSelected = isSelected(rows[rowIndex].systemCodeId);
    const labelId = `enhanced-table-checkbox-${rowIndex}`;

    return (
      <TableRow tabIndex={-1} key={rowIndex} aria-checked={isItemSelected}>
        <TableCell padding="checkbox">
          <div className={classes.grdRow} style={{ margin: 0 }}>
            <div className={classes.grdCellNone}>
              <Checkbox
                onClick={(event) =>
                  handleClick(event, rows[rowIndex].systemCodeId)
                }
                checked={isItemSelected}
                inputProps={{ 'aria-labelledby': labelId }}
              />
            </div>
            <div className={classes.grdCellNone}>
              <IconButton
                aria-label="delete"
                onClick={() => {
                  removeSystemCodeSingle(
                    systemCodeClient,
                    rows,
                    setRows,
                    rows[rowIndex].systemCodeId,
                    rows[rowIndex].type,
                    rows[rowIndex].description,
                    confirm
                  );
                }}
              >
                <DeleteIcon />
              </IconButton>
            </div>
            <div className={classes.grdCellNone}>
              <IconButton
                aria-label="edit"
                onClick={() => handleOpen(rows[rowIndex], false)}
              >
                <EditIcon />
              </IconButton>
            </div>
          </div>
        </TableCell>
        {sortedColumns
          .filter((v) => v.options.display)
          .map((col) => (
            <TableCell align={col.align || 'left'} key={col.name}>
              {rows[rowIndex][col.name]}
            </TableCell>
          ))}
      </TableRow>
    );
  };

  const showHideColumns = (column, action) => {
    setVisibles[column](action === 'remove' ? false : true);
  };

  const fileName =
    'SystemCode_' + moment().format('MMMM Do YYYY, h:mm:ss a') + '.csv';

  const options = {
    filterType: 'select',
    customRowRender: customTableRow,
    onColumnViewChange: showHideColumns,
    download: true,
    filter: true,
    search: false,
    print: false,
    sort: true,
    viewColumns: true,
    columnOrder: [2, 0, 1],
    selectableRowsHeader: false,
    count: page.totalRows,
    serverSide: true,
    rowsPerPage: page.displayedRows,
    onDownload: () => {
      document.getElementById('csvDL').click();
      return false;
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'filterChange': {
          let filterValues = [];

          for (let i = 0; i < tableState.filterList.length; i++) {
            if (tableState.filterList[i].length > 0) {
              filterValues.push({
                columnIndex: i,
                value: tableState.filterList[i][0],
              });
            } else {
              filterValues.push({
                columnIndex: i,
                value: tableState.filterList[i][0],
              });
            }
          }
          search(
            'filterChange',
            tableState.page,
            tableState.rowsPerPage,
            '',
            tableState.sortOrder.direction,
            filterValues
          );
          break;
        }
        case 'resetFilters': {
          search('search', 0, page.displayedRows);
          break;
        }
        case 'changePage': {
          search(
            'pageChange',
            tableState.page,
            tableState.rowsPerPage,
            '',
            '',
            page.filter
          );
          break;
        }
        case 'sort': {
          search(
            'sortChange',
            tableState.page,
            tableState.rowsPerPage,
            tableState.columns[tableState.activeColumn].name,
            tableState.sortOrder.direction,
            page.filter
          );
          break;
        }
        case 'changeRowsPerPage': {
          search(
            'pageChange',
            tableState.page,
            tableState.rowsPerPage,
            '',
            '',
            page.filter
          );
          break;
        }
        case 'propsUpdate': {
          return { ...tableState, count: page.totalRows };
        }
        default: {
          break;
        }
      }
    },
  };
  /*=========================================================================================
  End of table configuration
  ===========================================================================================*/
  const [rows, setRows] = useState([]);
  const [downloadableRows, setDownloadableRows] = useState([]);

  const handleSearch = (page) => {
    search('search', 0, page.displayedRows);
  };
  /*=========================================================================================
  Search input fields
  ===========================================================================================*/
  const [typeValue, setTypeValue] = React.useState('');
  const [currentPath, setCurrentPath] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showFilter, setShowFilter] = React.useState(true);

  useEffect(() => {
    const query = new URLSearchParams(params.location.search);
    const path = params.truepath;
    const data = {
      type: query.get('type'),
    };

    if (data.type) {
      setTypeValue(data.type);
    }

    setCurrentPath(path);
  }, [params]);
  /*=========================================================================================
  End of search input fields
  ===========================================================================================*/

  /*=========================================================================================
  Handles modal
  ===========================================================================================*/
  const [modalSystemCodeIDValue, setModalSystemCodeIDValue] = React.useState(
    ''
  );
  const [modalTypeValue, setModalTypeValue] = React.useState('');
  const [modalCodeValue, setModalCodeValue] = React.useState('');
  const [
    modalCodeDescriptionValue,
    setModalCodeDescriptionValue,
  ] = React.useState('');
  const [modalAddAction, setModalAddAction] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [modalTitleValue, setModalTitleValue] = React.useState('Add New');

  const handleOpen = (rowData, isAdd) => {
    if (isAdd) {
      setModalTypeValue('');
      setModalCodeValue('');
      setModalCodeDescriptionValue('');

      setModalAddAction(true);
      setModalTitleValue('Add New');
    } else {
      setModalSystemCodeIDValue(rowData.systemCodeId);
      setModalTypeValue(rowData.type);
      setModalCodeValue(rowData.code);
      setModalCodeDescriptionValue(rowData.description);

      setModalAddAction(false);
      setModalTitleValue('Edit');
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  /*=========================================================================================
  End of modal input fields
  ===========================================================================================*/

  /*=========================================================================================
  Table component
  ===========================================================================================*/
  return (
    <div className={classes.root}>
      <Box component="div" mt={5}>
        {showFilter ? (
          <div>
            <div className={classes.grdRow}>
              <div className={classes.grdCellNone} style={{ marginRight: 30 }}>
                {/* <TextField
                  className={classes.searchField}
                  label="Type"
                  placeholder="Type"
                  fullWidth={true}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={typeValue}
                  onChange={(e) => setTypeValue(e.target.value)}
                /> */}
                <SelectSystemCodeType
                  freeSolo={false}
                  name="type"
                  label="Type"
                  style={{ width: 300 }}
                  fullWidth={true}
                  value={typeValue}
                  onChange={(e) => {
                    setTypeValue(e.currentTarget.value);
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}
        <div className={classes.actionContainer}>
          <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<DeleteIcon />}
              onClick={() => {
                removeSystemCode(
                  systemCodeClient,
                  rows,
                  setRows,
                  rowID,
                  handleClick,
                  confirm
                );
              }}
            >
              Delete
            </Button>
          </div>
          <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => {
                handleOpen('', true);
              }}
            >
              Add New
            </Button>
          </div>
          <div className={classes.grdCellNone}>
            <SearchButton
              onClick={() => handleSearch(page)}
              loading={loading}
              showfilter={(status) => setShowFilter(status)}
            />
          </div>
        </div>
      </Box>
      <Box component="div" mt={2}>
        {downloadableRows && (
          <CSVLink
            id="csvDL"
            data={downloadableRows}
            filename={fileName}
            target="_blank"
          />
        )}
        <MuiThemeProvider theme={tableTheme()}>
          <MUIDataTable
            title={'System Codes'}
            data={rows}
            columns={columns}
            options={options}
          />
        </MuiThemeProvider>
      </Box>
      <Modal
        className={classes.modalBackdrop}
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <div className={classes.fadeModalMd}>
            <Typography
              id="transition-modal-title"
              className={classes.textBold}
              variant="h4"
              gutterBottom
            >
              {modalTitleValue} System Code
            </Typography>
            <Box id="transition-modal-description">
              <form className={classes.modalForm} noValidate autoComplete="off">
                <div className={classes.grdRow}>
                  <div className={classes.grdCell1}>
                    <TextField
                      required
                      label="Type"
                      placeholder="Type"
                      fullWidth={true}
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={modalTypeValue}
                      onChange={(e) => setModalTypeValue(e.target.value)}
                    />
                  </div>
                </div>
                <div className={classes.grdRow}>
                  <div className={classes.grdCell1} style={{ marginRight: 30 }}>
                    <TextField
                      label="Code"
                      placeholder="Code"
                      fullWidth={true}
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={modalCodeValue}
                      onChange={(e) => setModalCodeValue(e.target.value)}
                    />
                  </div>
                  <div className={classes.grdCell1}>
                    <TextField
                      label="Code Description"
                      placeholder="Code Description"
                      fullWidth={true}
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={modalCodeDescriptionValue}
                      onChange={(e) =>
                        setModalCodeDescriptionValue(e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className={classes.modalFooter}>
                  <div
                    className={classes.grdCellNone}
                    style={{ marginRight: 10 }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </div>
                  <div className={classes.grdCellNone}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => {
                        saveSystemCode(
                          systemCodeClient,
                          rows,
                          modalAddAction,
                          handleClose,
                          modalSystemCodeIDValue,
                          modalTypeValue,
                          modalCodeValue,
                          modalCodeDescriptionValue
                        );
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </form>
            </Box>
          </div>
        </Fade>
      </Modal>
    </div>
  );
}
